import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Building2, FileText, Download, X } from "lucide-react";
import { API } from "@/lib/site";
import { CoverUpload } from "@/components/CoverUpload";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#060D17]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";
const labelCls = "font-mono-x mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-400";

const INITIAL = { name: "", kind: "Insurer", logo_url: "", forms: [] };

export const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(INITIAL);
  const [formTitle, setFormTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const headers = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("hs_admin_token")}` } });

  const load = useCallback(() => {
    axios
      .get(`${API}/partners`)
      .then(({ data }) => setPartners(data))
      .catch(() => toast.error("Could not load companies."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/partners/${editingId}`, form, headers());
        toast.success("Company updated.");
      } else {
        await axios.post(`${API}/partners`, form, headers());
        toast.success("Company added.");
      }
      setForm(INITIAL);
      setFormTitle("");
      setEditingId(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save company.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, kind: p.kind, logo_url: p.logo_url || "", forms: p.forms || [] });
    document.querySelector('[data-testid="admin-partners-section"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(INITIAL);
    setFormTitle("");
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and its form links? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/partners/${p.id}`, headers());
      toast.success("Company deleted.");
      if (editingId === p.id) cancelEdit();
      load();
    } catch {
      toast.error("Could not delete company.");
    }
  };

  const removeForm = (index) => {
    setForm((f) => ({ ...f, forms: f.forms.filter((_, i) => i !== index) }));
  };

  return (
    <section data-testid="admin-partners-section" className="mt-2">
      <h2 className="font-display flex items-center gap-3 text-xl font-bold text-white">
        <Building2 className="h-5 w-5 text-teal-400" />
        Insurance Companies & TPAs
      </h2>
      <p className="mt-1 text-sm text-slate-400">
        Add companies with their logo and official pre-authorization forms. Logos appear in the
        homepage network strip; forms appear on the Resources page grouped by company.
      </p>

      <form onSubmit={save} className="mt-6 rounded-2xl border border-white/5 bg-[#0B192C]/60 p-7">
        {editingId && (
          <p data-testid="partner-editing-banner" className="font-mono-x mb-5 text-[10px] uppercase tracking-[0.25em] text-amber-500">
            Editing existing company — save to update or cancel below
          </p>
        )}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="p-name">Company Name *</label>
            <input id="p-name" data-testid="partner-form-name" required minLength={2}
              placeholder="e.g. Star Health & Allied Insurance" className={inputCls}
              value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls} htmlFor="p-kind">Type *</label>
            <select id="p-kind" data-testid="partner-form-kind" className={inputCls}
              value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}>
              <option value="Insurer">Insurance Company</option>
              <option value="TPA">TPA (Third Party Administrator)</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Company Logo (image)</label>
            <div className="flex flex-wrap items-center gap-4">
              {form.logo_url && (
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white p-3">
                  <img src={form.logo_url} alt="logo preview" data-testid="partner-logo-preview" className="h-10 max-w-[140px] object-contain" />
                  <button type="button" data-testid="partner-logo-clear"
                    onClick={() => setForm((f) => ({ ...f, logo_url: "" }))}
                    className="text-slate-500 transition-colors hover:text-red-500" aria-label="Remove logo">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <CoverUpload
                testId="partner-logo-upload"
                onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))}
                label={form.logo_url ? "Replace logo" : "Upload logo"}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>Pre-Authorization Forms (PDF)</label>
            {form.forms.length > 0 && (
              <ul className="mb-4 space-y-2">
                {form.forms.map((doc, i) => (
                  <li key={i} data-testid={`partner-form-item-${i}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-[#060D17]/60 px-4 py-3">
                    <span className="flex min-w-0 items-center gap-2.5 text-sm text-slate-200">
                      <FileText className="h-4 w-4 shrink-0 text-amber-500" />
                      <span className="truncate">{doc.title}</span>
                      <span className="shrink-0 text-xs text-slate-500">({doc.filename})</span>
                    </span>
                    <button type="button" data-testid={`partner-form-remove-${i}`} onClick={() => removeForm(i)}
                      className="shrink-0 text-slate-500 transition-colors hover:text-red-400" aria-label="Remove form">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                data-testid="partner-form-title-input"
                placeholder="Form title, e.g. Cashless Pre-Auth Form"
                className={`${inputCls} sm:flex-1`}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
              <CoverUpload
                testId="partner-pdf-upload"
                accept="application/pdf"
                label="Upload form (PDF)"
                onUploaded={(url, filename) => {
                  const title = formTitle.trim() || filename.replace(/\.[^.]+$/, "");
                  setForm((f) => ({ ...f, forms: [...f.forms, { title, url, filename }] }));
                  setFormTitle("");
                }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Form ko yaad rakhne ke liye pehle title likhein, phir PDF upload karein — phir neeche "Save" dabana na bhoolein.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button type="submit" disabled={saving} data-testid="partner-form-submit"
            className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-[#060D17] transition-all duration-300 hover:bg-teal-400 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? "Saving..." : editingId ? "Save Changes" : "Add Company"}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="partner-cancel-edit"
              className="rounded-full border border-white/10 px-6 py-3.5 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-red-500/40 hover:text-red-400">
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {partners.length === 0 && (
          <p className="rounded-2xl border border-white/5 bg-[#0B192C]/40 p-6 text-sm text-slate-500">
            No companies added yet — add the first one above.
          </p>
        )}
        {partners.map((p) => (
          <div key={p.id} data-testid={`admin-partner-row-${p.id}`}
            className="flex flex-col gap-4 rounded-2xl border border-white/5 bg-[#0B192C]/40 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              {p.logo_url ? (
                <span className="flex h-12 w-20 shrink-0 items-center justify-center rounded-lg bg-white p-1.5">
                  <img src={p.logo_url} alt={p.name} className="max-h-9 max-w-full object-contain" />
                </span>
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-teal-500/10">
                  <Building2 className="h-5 w-5 text-teal-400" />
                </span>
              )}
              <div className="min-w-0">
                <p className="font-display truncate text-sm font-bold text-white">{p.name}</p>
                <p className="font-mono-x mt-1 text-[10px] uppercase tracking-[0.2em] text-teal-400">
                  {p.kind} · {(p.forms || []).length} form{(p.forms || []).length === 1 ? "" : "s"}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => startEdit(p)} data-testid={`partner-edit-${p.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-amber-500/40 hover:text-amber-400">
                <Pencil className="h-3.5 w-3.5" />
                Edit / Forms
              </button>
              <button onClick={() => remove(p)} data-testid={`partner-delete-${p.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-red-500/40 hover:text-red-400">
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
